// ChartDataCoordinator: implementation with race token pattern for symbol/granularity switching

import { IFeedService, ISymbolService, TQuote, TMarketStatusChanges } from '@smartcharts/core';
// Optional indicator manager integration (Phase 7 skeleton)
import { IIndicatorManager } from '../indicators/manager/IndicatorManager';
import { Quote as IndicatorQuote } from '../indicators/compute/IndicatorCompute';
import { IChartEngine } from '../engine/IChartEngine';
import {
    IChartDataCoordinator,
    SymbolChangeParams,
    CoordinatorState,
    SymbolChangeListener,
    UnsubscribeFn,
} from './IChartDataCoordinator';

export interface ChartDataCoordinatorOptions {
    feedService: IFeedService;
    engine: IChartEngine;
    onError?: (error: string) => void;
    symbolService?: ISymbolService;
    indicatorManager?: IIndicatorManager; // optional indicators integration
}

export class ChartDataCoordinator implements IChartDataCoordinator {
    private _feedService: IFeedService;
    private _engine: IChartEngine;
    private _onError?: (error: string) => void;
    private _state: CoordinatorState = { isLoading: false };
    private _listeners: Set<SymbolChangeListener> = new Set();
    private _raceToken = 0;
    private _liveUnsubscribe?: UnsubscribeFn;
    private _symbolService?: ISymbolService;
    private _marketUnsubscribe?: () => void;
    private _indicatorManager?: IIndicatorManager;

    constructor(opts: ChartDataCoordinatorOptions) {
        this._feedService = opts.feedService;
        this._engine = opts.engine;
        this._onError = opts.onError;
        this._symbolService = opts.symbolService;
        this._indicatorManager = opts.indicatorManager;
        // Subscribe to market status changes to update engine state for the current symbol
        if (this._symbolService) {
            this._marketUnsubscribe = this._symbolService.onMarketStatus((changes: TMarketStatusChanges) => {
                const current = this._state.currentSymbol?.symbol;
                if (!current) return;
                const isOpen = changes[current];
                if (typeof isOpen === 'boolean') {
                    this._engine.setSymbolClosed(!isOpen);
                }
            });
        }
    }

    async setSymbolAndGranularity(params: SymbolChangeParams): Promise<void> {
        const { symbol, granularity } = params;
        const targetGranularity = granularity !== undefined ? granularity : this._state.currentGranularity || 0;

        // Check if already on this symbol+granularity (idempotent)
        if (
            this._state.currentSymbol?.symbol === symbol.symbol &&
            this._state.currentGranularity === targetGranularity &&
            !this._state.error
        ) {
            return;
        }

        const token = ++this._raceToken;
        this._updateState({ isLoading: true, error: undefined });

        try {
            // Step 1: Unsubscribe previous live feed
            if (this._liveUnsubscribe) {
                this._liveUnsubscribe();
                this._liveUnsubscribe = undefined;
            }
            if (this._state.currentSymbol) {
                this._feedService.unsubscribe(
                    this._state.currentSymbol.symbol,
                    this._state.currentGranularity || 0
                );
            }

            // Step 2: Update engine with new symbol metadata (pip/precision)
            const decimalPlaces = symbol.decimalPlaces ?? this._symbolService?.getDecimalPlaces(symbol.symbol) ?? 2;
            this._engine.setSymbol(
                {
                    symbol: symbol.symbol,
                    name: symbol.name,
                    pip: decimalPlaces,
                },
                decimalPlaces
            );

            // Step 3: Fetch initial history
            const historyResult = await this._feedService.fetchHistory({
                symbol: symbol.symbol,
                granularity: targetGranularity,
                count: 1000,
            });

            // Race check: abort if a newer request started
            if (token !== this._raceToken) {
                return;
            }

            if (historyResult.error) {
                this._updateState({
                    isLoading: false,
                    error: typeof historyResult.error === 'string' ? historyResult.error : historyResult.error.message,
                });
                if (this._onError) {
                    this._onError(
                        typeof historyResult.error === 'string' ? historyResult.error : historyResult.error.message
                    );
                }
                return;
            }

            // Step 4: Apply history to engine
            this._engine.applyTickBatch(historyResult.quotes);
            // Feed indicator manager history (convert quotes)
            if (this._indicatorManager) {
                const converted: IndicatorQuote[] = historyResult.quotes.map(q => ({
                    epoch: (q.DT as Date)?.getTime() ? ((q.DT as Date).getTime() / 1000) | 0 : Date.parse(q.Date)/1000,
                    open: q.Open,
                    high: q.High,
                    low: q.Low,
                    close: q.Close,
                    volume: q.Volume,
                }));
                this._indicatorManager.ingestHistory(converted);
                await this._indicatorManager.recomputeAll();
            }

            // Step 5: Subscribe to live updates
            this._liveUnsubscribe = await this._feedService.subscribeLive({
                symbol: symbol.symbol,
                granularity: targetGranularity,
                onTick: (tick: TQuote) => {
                    // Only apply ticks if still on same symbol (avoid stale ticks after switch)
                    if (this._state.currentSymbol?.symbol === symbol.symbol) {
                        try { console.debug('Coordinator live tick', symbol.symbol, 'g', targetGranularity, 't', tick.Date || tick.DT?.toISOString()); } catch {}
                        this._engine.applyTickBatch([tick]);
                        if (this._indicatorManager) {
                            const converted: IndicatorQuote = {
                                epoch: (tick.DT as Date)?.getTime() ? ((tick.DT as Date).getTime() / 1000) | 0 : Date.parse(tick.Date)/1000,
                                open: tick.Open,
                                high: tick.High,
                                low: tick.Low,
                                close: tick.Close,
                                volume: tick.Volume,
                            };
                            this._indicatorManager.ingestTick(converted);
                        }
                    }
                },
                onError: err => {
                    if (this._onError) this._onError(err.message);
                },
            });

            // Final race check before committing state
            if (token !== this._raceToken) {
                if (this._liveUnsubscribe) this._liveUnsubscribe();
                return;
            }

            // Step 6: Update coordinator state (success)
            this._updateState({
                currentSymbol: symbol,
                currentGranularity: targetGranularity,
                isLoading: false,
                error: undefined,
            });
            // Recompute indicators again post state commit (ensures layout accurate if symbol precision changed)
            if (this._indicatorManager) await this._indicatorManager.recomputeAll();
        } catch (err: any) {
            if (token !== this._raceToken) return; // ignore stale errors
            const errorMsg = err.message || 'Symbol change failed';
            this._updateState({ isLoading: false, error: errorMsg });
            if (this._onError) this._onError(errorMsg);
        }
    }

    async refresh(): Promise<void> {
        if (!this._state.currentSymbol) return;
        await this.setSymbolAndGranularity({
            symbol: this._state.currentSymbol,
            granularity: this._state.currentGranularity,
        });
    }

    getState(): CoordinatorState {
        return { ...this._state };
    }

    onStateChange(listener: SymbolChangeListener): UnsubscribeFn {
        this._listeners.add(listener);
        return () => this._listeners.delete(listener);
    }

    cancelPending(): void {
        this._raceToken++;
        this._updateState({ isLoading: false });
    }

    destroy(): void {
        this.cancelPending();
        if (this._liveUnsubscribe) {
            this._liveUnsubscribe();
            this._liveUnsubscribe = undefined;
        }
        this._feedService.unsubscribeAll();
        this._listeners.clear();
        if (this._marketUnsubscribe) {
            this._marketUnsubscribe();
            this._marketUnsubscribe = undefined;
        }
        // No explicit cleanup required for indicatorManager in skeleton (stateless)
    }

    private _updateState(partial: Partial<CoordinatorState>): void {
        this._state = { ...this._state, ...partial };
        for (const listener of this._listeners) {
            listener(this.getState());
        }
    }

    async onLocaleChanged(locale: string): Promise<void> {
        if (!this._symbolService) return;
        const prev = this._state.currentSymbol;
        const prevDec = prev ? (prev.decimalPlaces ?? this._symbolService.getDecimalPlaces(prev.symbol)) : undefined;
        await this._symbolService.setLocale(locale);
        const next = prev ? this._symbolService.getSymbol(prev.symbol) : undefined;
        if (!prev || !next) return;
        const nextDec = next.decimal_places;
        const nameChanged = prev.name !== next.name;
        const precisionChanged = prevDec !== nextDec;
        // Update coordinator state name to reflect locale change
        if (nameChanged || precisionChanged) {
            this._state.currentSymbol = {
                symbol: next.symbol,
                name: next.name,
                decimalPlaces: nextDec,
                exchange_is_open: !!next.exchange_is_open,
            };
        }
        if (precisionChanged) {
            // Update engine symbol settings without resetting data; do not fetch history.
            this._engine.setSymbol({ symbol: next.symbol, name: next.name, pip: nextDec }, nextDec);
        }
        // Notify listeners of state change so UI categories rebuild can happen externally
        this._updateState({ ...this._state });
    }
}

export default ChartDataCoordinator;
